import { cn } from '@/lib/utils';
import { MessageCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

const FB_BLUE = '#1877F2';

interface CommentHeaderProps {
  title: string;
  commentsCount: number;
  refreshInterval?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  compact?: boolean;
}

export function CommentHeader({
  title,
  commentsCount,
  refreshInterval = 20,
  onRefresh,
  isRefreshing = false,
  compact = false,
}: CommentHeaderProps) {
  const [isEnlarged, setIsEnlarged] = useState(false);

  useEffect(() => {
    if (!onRefresh || isRefreshing) return;

    const timer = setInterval(onRefresh, refreshInterval * 1000);
    return () => clearInterval(timer);
  }, [refreshInterval, onRefresh, isRefreshing]);

  const handleHeaderClick = () => {
    setIsEnlarged((prev) => !prev);
    onRefresh?.();
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 min-w-0',
        compact ? 'p-0' : 'gap-3 p-1',
      )}
    >
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <button
          onClick={handleHeaderClick}
          style={{ backgroundColor: FB_BLUE }}
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full text-white shadow-lg transition-all duration-300 ease-in-out hover:brightness-110',
            compact ? 'h-8 w-8' : isEnlarged ? 'h-14 w-14' : 'h-10 w-10',
          )}
        >
          <MessageCircle
            className={cn(
              'transition-all duration-300',
              compact ? 'h-4 w-4' : isEnlarged ? 'h-7 w-7' : 'h-5 w-5',
            )}
          />
        </button>

        <div className="min-w-0 truncate">
          <div
            className={cn(
              'truncate font-bold tracking-tight text-slate-900',
              compact ? 'text-sm' : 'text-[15px]',
            )}
          >
            {title}
          </div>

          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500 sm:gap-2 sm:text-[12px]">
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
              {commentsCount} total
            </span>

            {isRefreshing && (
              <>
                <div className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#1877F2] animate-pulse">
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-[#1877F2] border-t-transparent animate-spin" />
                  Fetching...
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
